<template>
  <div
    v-loading="showOverlayLoadingIndicator && loading"
    class="select-multi-user"
  >
    <BSelect
      ref="inputRef"
      v-model="selectedUserIds"
      :disabled="disabled || (showOverlayLoadingIndicator && loading)"
      :items="userItems"
      :clearable="clearable"
      :placeholder="placeholder"
      :teleported="teleported"
      :loading="loading"
      :placement="placement"
      multiple
      :collapse-tags="collapseTags"
      :collapse-tags-tooltip="collapseTagsTooltip"
      :multiple-limit="multipleLimit"
      item-text="name"
      item-value="id"
      filterable
      class="type-select"
      :class="{ 'flexible-width': flexibleWidth }"
      @blur="handleBlur"
    />
  </div>
</template>

<script lang="ts" setup>
import type { Placement } from '@popperjs/core';
import { computed, nextTick, onBeforeMount, ref, watch, useId } from 'vue';
import { useUsers } from '@/composable/user/user/users';
import { useWait } from '@/composable/vue-wait';
import { TOptions } from '../atoms/BSelect.vue';

type TProps = {
  placeholder?: string;
  clearable?: boolean;
  onlyActiveUsers?: boolean;
  teleported?: boolean;
  disabled?: boolean;
  flexibleWidth?: boolean;
  showOverlayLoadingIndicator?: boolean;
  placement?: Placement;
  collapseTags?: boolean;
  collapseTagsTooltip?: boolean;
  multipleLimit?: number;
};
type TEmit = {
  'update:modelValue': [modelValue: number[]];
  'blur': [];
};

const props = withDefaults(defineProps<TProps>(), {
  placeholder: undefined,
  clearable: false,
  onlyActiveUsers: false,
  teleported: true,
  flexibleWidth: false,
  showOverlayLoadingIndicator: true,
  placement: undefined,
  collapseTags: true,
  collapseTagsTooltip: true,
  multipleLimit: 0,
});

const emit = defineEmits<TEmit>();
const { wait } = useWait();
const componentId = useId();
const { users, activeUsers, fetchUsers, keyOfFetchUsers } = useUsers(() => false, () => componentId);
const loading = computed(() => {
  return wait.is(keyOfFetchUsers.value);
});

const modelValue = defineModel<number[]>({ default: () => [] });
const inputRef = ref<{ focus: () => void } | null>(null);
const userItems = ref<TOptions>([]);
const fetchPromise = ref<Promise<unknown>>();

const selectedUserIds = computed({
  get() {
    return modelValue.value ?? [];
  },
  set(value: number[]) {
    emit('update:modelValue', value ?? []);
  },
});

onBeforeMount(async () => {
  fetchPromise.value = fetchUsers();
  await fetchPromise.value;
});
watch(users, () => {
  userItems.value = makeUserItems();
});

const makeUserItems = () => {
  const targetUsers = props.onlyActiveUsers ? activeUsers.value : users.value;
  return [...targetUsers];
};

const handleBlur = () => {
  emit('blur');
};

const focus = async () => {
  await fetchPromise.value;
  await nextTick();
  inputRef.value?.focus();
};

defineExpose({
  focus,
});
</script>

<style lang="scss" scoped>
.select-multi-user {
  .type-select {
    width: 160px;

    &.flexible-width {
      width: 100%;
    }
  }
}
</style>
